import React, {
  createContext,
  useContext,
  useRef,
  useState,
  useEffect,
} from "react";
import { Snackbar, Alert } from "@mui/material";

/**
 * @typedef {"success" | "error" | "info" | "warning" | "none"} ToastType
 * - "none" = no icon (uses info color by default)
 */

/**
 * @typedef {Object} ToastConfig
 * @property {ToastType} [type] - severity/type of the toast (default: "success")
 * @property {number | "never"} [timeout] - milliseconds before auto-hide (default: 5000)
 * @property {{ vertical: "top" | "bottom", horizontal: "left" | "center" | "right"}} [anchorOrigin] - default {vertical:"bottom",horizontal:"right"}
 * @property {number} [spacing] - vertical spacing for stacked toasts (default 72)
 * @property {number} [offset] - base offset (px) from edge (default 16)
 * @property {object} [snackbarProps] - props for MUI Snackbar
 * @property {object} [alertProps] - props for MUI Alert
 * @property {boolean} [closeOnClickAway] - default false; if false, clicking outside won't close the toast
 */

/**
 * @typedef {Object} ToastItem
 * @property {number} id
 * @property {string} message
 * @property {ToastConfig} config
 * @property {number} [timeoutId]
 */

/**
 * @typedef {Object} ToastContextValue
 * @property {(message: string, toastConfig?: ToastConfig) => number} toast
 * @property {(toastId: number) => void} hideToast
 */

const ToastContext = createContext(
  /** @type {ToastContextValue | null} */ (null)
);

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within a ToastProvider");
  return { toast: ctx.toast, hideToast: ctx.hideToast };
}

/**
 * @param {{children: React.ReactNode}} props
 */
export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState(/** @type {ToastItem[]} */ ([]));
  const nextIdRef = useRef(0);
  const timeoutsRef = useRef(/** @type {{[id:number]: number}} */ ({}));

  function hideToast(toastId) {
    const tid = timeoutsRef.current[toastId];
    if (typeof tid === "number") {
      clearTimeout(tid);
      delete timeoutsRef.current[toastId];
    }
    setToasts((prev) => prev.filter((t) => t.id !== toastId));
  }

  /**
   * @param {string} message
   * @param {ToastConfig} [toastConfig]
   */
  function toast(message, toastConfig = {}) {
    const id = ++nextIdRef.current;
    const config = {
      type: "success",
      timeout: 5000,
      anchorOrigin: { vertical: "bottom", horizontal: "right" },
      spacing: 72,
      offset: 16,
      snackbarProps: {},
      alertProps: {},
      closeOnClickAway: false,
      ...toastConfig,
    };

    if (config.timeout !== "never") {
      const timeoutId = window.setTimeout(() => {
        hideToast(id);
      }, config.timeout);
      timeoutsRef.current[id] = timeoutId;
    }

    setToasts((prev) => [...prev, { id, message, config }]);
    return id;
  }

  useEffect(() => {
    return () => {
      Object.values(timeoutsRef.current).forEach((tid) => {
        if (typeof tid === "number") clearTimeout(tid);
      });
      timeoutsRef.current = {};
    };
  }, []);

  return (
    <ToastContext.Provider value={{ toast, hideToast }}>
      {children}
      {toasts.map((t, index) => {
        const {
          anchorOrigin,
          spacing,
          offset,
          type,
          snackbarProps,
          alertProps,
          closeOnClickAway,
        } = t.config;

        const isBottom = anchorOrigin.vertical === "bottom";
        const isRight = anchorOrigin.horizontal === "right";
        const primaryOffset = offset + index * spacing;

        const sxPosition = {
          position: "fixed",
          ...(isRight ? { right: offset } : { left: offset }),
          ...(isBottom ? { bottom: primaryOffset } : { top: primaryOffset }),
        };

        const showIcon = type !== "none";
        const alertSeverity = type === "none" ? "info" : type;

        return (
          <Snackbar
            key={t.id}
            open
            anchorOrigin={anchorOrigin}
            onClose={(event, reason) => {
              if (reason === "clickaway" && !closeOnClickAway) return;
              hideToast(t.id);
            }}
            {...snackbarProps}
            sx={{ ...sxPosition, ...(snackbarProps.sx || {}) }}
          >
            <Alert
              icon={showIcon ? undefined : false}
              onClose={() => hideToast(t.id)}
              severity={alertSeverity}
              sx={{ width: "100%", ...(alertProps.sx || {}) }}
              {...alertProps}
            >
              {t.message}
            </Alert>
          </Snackbar>
        );
      })}
    </ToastContext.Provider>
  );
}
